import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/useToast';
import { useModal } from '../hooks/useModal';
import { doc, updateDoc } from 'firebase/firestore';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import { deleteRemoteAuthUser, updateRemoteAuthUser } from '../services/cloudFunctions';
import { db, auth } from '../firebaseConfig';
import Spinner from '../components/common/Spinner/Spinner';
import { FaEye, FaEyeSlash, FaKey } from 'react-icons/fa';
import './MeuPerfil.css';

const validatePasswordPolicy = (val) => {
    if (!val) return 'A senha é obrigatória.';
    if (val.length < 12) return 'A senha deve ter no mínimo 12 caracteres.';
    if (!/[A-Z]/.test(val)) return 'A senha deve conter pelo menos uma letra maiúscula.';
    if (!/[a-z]/.test(val)) return 'A senha deve conter pelo menos uma letra minúscula.';
    if (!/[0-9]/.test(val)) return 'A senha deve conter pelo menos um número.';
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>?]/.test(val)) return 'A senha deve conter pelo menos um caractere especial.';
    return true;
};

const MeuPerfil = () => {
    const { currentUser, logout, refreshUserProfile } = useAuth();
    const { addToast } = useToast();
    const { showModal } = useModal();

    const [nome, setNome] = useState('');
    const [atuaComoArticulador, setAtuaComoArticulador] = useState(false);
    const [atuaComoGestor, setAtuaComoGestor] = useState(false);
    const [loading, setLoading] = useState(false);

    // Estados para Alteração de Senha Própria
    const [senhaAtual, setSenhaAtual] = useState('');
    const [novaSenha, setNovaSenha] = useState('');
    const [confirmarNovaSenha, setConfirmarNovaSenha] = useState('');
    const [showAtual, setShowAtual] = useState(false);
    const [showNova, setShowNova] = useState(false);
    const [showConfirmar, setShowConfirmar] = useState(false);
    const [loadingSenha, setLoadingSenha] = useState(false);

    useEffect(() => {
        if (currentUser) {
            setNome(currentUser.nome || currentUser.displayName || '');
            setAtuaComoArticulador(currentUser.atuaComoArticulador === true);
            setAtuaComoGestor(currentUser.atuaComoGestor === true);
        }
    }, [currentUser]);

    const handleSave = async (e) => {
        e.preventDefault();
        if (!nome.trim()) {
            addToast('O nome não pode estar vazio.', 'error');
            return;
        }

        setLoading(true);
        try {
            if (currentUser.perfil === 'ADM.ORG') {
                await updateRemoteAuthUser({
                    uid: currentUser.uid,
                    nome: nome.trim(),
                    perfil: currentUser.perfil,
                    idOrganizacao: currentUser.idOrganizacao,
                    atuaComoArticulador,
                    atuaComoGestor,
                });
                if (refreshUserProfile) {
                    await refreshUserProfile();
                }
            } else {
                const userRef = doc(db, 'usuarios', currentUser.uid);
                await updateDoc(userRef, { nome: nome.trim() });
                if (refreshUserProfile) {
                    await refreshUserProfile();
                }
            }

            addToast('Perfil atualizado com sucesso!', 'success');
        } catch (error) {
            console.error('Erro ao atualizar perfil:', error);
            addToast('Erro ao atualizar perfil.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();

        if (!senhaAtual || !novaSenha || !confirmarNovaSenha) {
            addToast('Preencha todos os campos de senha.', 'error');
            return;
        }

        if (novaSenha !== confirmarNovaSenha) {
            addToast('A nova senha e a confirmação são diferentes.', 'error');
            return;
        }

        if (novaSenha === senhaAtual) {
            addToast('A nova senha deve ser diferente da senha atual.', 'error');
            return;
        }

        const policyCheck = validatePasswordPolicy(novaSenha);
        if (policyCheck !== true) {
            addToast(policyCheck, 'error');
            return;
        }

        setLoadingSenha(true);
        try {
            const user = auth.currentUser;
            if (!user || !user.email) {
                throw new Error('Usuário sem sessão ativa.');
            }

            // Reautenticação obrigatória com a senha atual
            const credential = EmailAuthProvider.credential(user.email, senhaAtual);
            await reauthenticateWithCredential(user, credential);

            // Atualizar senha no Firebase Authentication
            await updatePassword(user, novaSenha);

            // Limpar imediatamente os campos
            setSenhaAtual('');
            setNovaSenha('');
            setConfirmarNovaSenha('');

            addToast('Senha alterada com sucesso! Faça login novamente com sua nova senha.', 'success');

            // Encerrar sessão e redirecionar
            setTimeout(async () => {
                await logout();
            }, 1200);

        } catch (error) {
            console.error('Erro ao alterar senha:', error);
            let msg = 'Erro ao alterar senha.';
            if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
                msg = 'A senha atual informada está incorreta.';
            } else if (error.code === 'auth/too-many-requests') {
                msg = 'Muitas tentativas malsucedidas. Aguarde alguns minutos e tente novamente.';
            } else if (error.code === 'auth/requires-recent-login') {
                msg = 'Sua sessão expirou. Faça login novamente.';
            } else if (error.message) {
                msg = error.message;
            }
            addToast(msg, 'error');
        } finally {
            setLoadingSenha(false);
        }
    };

    const handleDeleteAccount = () => {
        showModal({
            title: 'Excluir Conta',
            message: 'Tem certeza que deseja excluir sua conta permanentemente? Todos os seus dados serão removidos. Esta ação não pode ser desfeita.',
            confirmText: 'Sim, excluir minha conta',
            cancelText: 'Cancelar',
            onConfirm: async () => {
                setLoading(true);
                try {
                    await deleteRemoteAuthUser({ uid: currentUser.uid });
                    addToast('Sua conta foi excluída com sucesso.', 'success');
                    await logout();
                } catch (error) {
                    console.error('Erro ao excluir conta:', error);
                    let msg = 'Erro ao excluir conta: ' + (error?.message || 'Tente novamente.');
                    if (error?.code === 'failed-precondition' || error?.message?.includes('unico ADM MASTER')) {
                        msg = 'Operação cancelada: Não é possível excluir o único ADM MASTER do sistema.';
                    }
                    addToast(msg, 'error');
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    if (loading) return <Spinner text="Processando..." />;

    return (
        <div className="resource-management-container meu-perfil-view">
            <header className="resource-header">
                <div>
                    <h1>Meu Perfil</h1>
                    <p>Gerencie seus dados e preferências de conta.</p>
                </div>
            </header>

            <form onSubmit={handleSave} className="perfil-form-card">
                <h2>Informações Pessoais</h2>
                <div className="perfil-form-group">
                    <label htmlFor="email">E-mail</label>
                    <input
                        type="email"
                        id="email"
                        value={currentUser?.email || ''}
                        disabled
                        title="O e-mail não pode ser alterado."
                    />
                </div>

                <div className="perfil-form-group">
                    <label htmlFor="nome">Nome Completo</label>
                    <input
                        type="text"
                        id="nome"
                        value={nome}
                        onChange={(e) => setNome(e.target.value)}
                        placeholder="Seu nome completo"
                    />
                </div>

                <div className="perfil-form-group">
                    <label>Perfil de Acesso</label>
                    <input
                        type="text"
                        value={currentUser?.perfil || 'Usuário'}
                        disabled
                    />
                </div>

                {currentUser?.idOrganizacao && (
                    <div className="perfil-form-group">
                        <label>ID da Organização</label>
                        <input
                            type="text"
                            value={currentUser.idOrganizacao}
                            disabled
                        />
                    </div>
                )}

                {currentUser?.perfil === 'ADM.ORG' && (
                    <div className="perfil-roles-section">
                        <h3>Papéis Acumulados na Organização</h3>
                        <p className="perfil-section-desc">
                            Como Administrador Organizacional, você pode ativar papéis adicionais para atuar diretamente nas etapas do iESGo:
                        </p>

                        <div className="perfil-role-checkbox-card">
                            <label className="perfil-role-label">
                                <input
                                    type="checkbox"
                                    checked={atuaComoGestor}
                                    onChange={(e) => setAtuaComoGestor(e.target.checked)}
                                />
                                <div>
                                    <strong>Atuar também como membro do colegiado</strong>
                                    <span>Permite que você seja selecionado como participante e vote nos ciclos de Priorização Colegiada.</span>
                                </div>
                            </label>
                        </div>

                        <div className="perfil-role-checkbox-card">
                            <label className="perfil-role-label">
                                <input
                                    type="checkbox"
                                    checked={atuaComoArticulador}
                                    onChange={(e) => setAtuaComoArticulador(e.target.checked)}
                                />
                                <div>
                                    <strong>Atuar também como articulador</strong>
                                    <span>Permite receber atribuição de questões do diagnóstico e preencher respostas.</span>
                                </div>
                            </label>
                        </div>
                    </div>
                )}

                {currentUser?.perfil === 'ARTICULADOR' && currentUser?.atuaComoGestor && (
                    <div className="perfil-roles-section">
                        <h3>Papéis na Organização</h3>
                        <div className="perfil-role-info-badge">
                            ✓ Membro do Colegiado habilitado pelo Administrador da sua instituição (você pode votar na Priorização Colegiada).
                        </div>
                    </div>
                )}

                <div className="perfil-actions">
                    <button type="submit" className="btn-salvar">Salvar Alterações</button>
                </div>
            </form>

            {currentUser?.authMethod === 'magic_link' ? (
                <div className="perfil-form-card senha-section">
                    <h2><FaKey style={{ marginRight: '8px' }} /> Acesso</h2>
                    <p className="perfil-section-desc">
                        Sua conta não usa senha — o acesso é feito por um link enviado ao seu e-mail,
                        válido para um único login. Se precisar entrar em outro dispositivo ou sua
                        sessão expirar, peça um novo link em <Link to="/acessar-por-link">Acessar por link</Link>.
                    </p>
                </div>
            ) : (
                <form onSubmit={handleChangePassword} className="perfil-form-card senha-section">
                    <h2><FaKey style={{ marginRight: '8px' }} /> Alterar Senha</h2>
                    <p className="perfil-section-desc">Altere sua senha de acesso. Por segurança, você precisará confirmar a senha atual.</p>

                    <div className="perfil-form-group">
                        <label htmlFor="senhaAtual">Senha Atual</label>
                        <div className="password-input-wrapper">
                            <input
                                type={showAtual ? 'text' : 'password'}
                                id="senhaAtual"
                                autoComplete="current-password"
                                value={senhaAtual}
                                onChange={(e) => setSenhaAtual(e.target.value)}
                                placeholder="Digite sua senha atual"
                                disabled={loadingSenha}
                            />
                            <button
                                type="button"
                                className="toggle-visibility-btn"
                                onClick={() => setShowAtual(!showAtual)}
                                title={showAtual ? 'Ocultar' : 'Exibir'}
                            >
                                {showAtual ? <FaEyeSlash /> : <FaEye />}
                            </button>
                        </div>
                    </div>

                    <div className="perfil-form-group">
                        <label htmlFor="novaSenha">Nova Senha</label>
                        <div className="password-input-wrapper">
                            <input
                                type={showNova ? 'text' : 'password'}
                                id="novaSenha"
                                autoComplete="new-password"
                                value={novaSenha}
                                onChange={(e) => setNovaSenha(e.target.value)}
                                placeholder="Mínimo 12 caracteres (ex: Abc#12345678)"
                                disabled={loadingSenha}
                            />
                            <button
                                type="button"
                                className="toggle-visibility-btn"
                                onClick={() => setShowNova(!showNova)}
                                title={showNova ? 'Ocultar' : 'Exibir'}
                            >
                                {showNova ? <FaEyeSlash /> : <FaEye />}
                            </button>
                        </div>
                    </div>

                    <div className="perfil-form-group">
                        <label htmlFor="confirmarNovaSenha">Confirmar Nova Senha</label>
                        <div className="password-input-wrapper">
                            <input
                                type={showConfirmar ? 'text' : 'password'}
                                id="confirmarNovaSenha"
                                autoComplete="new-password"
                                value={confirmarNovaSenha}
                                onChange={(e) => setConfirmarNovaSenha(e.target.value)}
                                placeholder="Confirme a nova senha"
                                disabled={loadingSenha}
                            />
                            <button
                                type="button"
                                className="toggle-visibility-btn"
                                onClick={() => setShowConfirmar(!showConfirmar)}
                                title={showConfirmar ? 'Ocultar' : 'Exibir'}
                            >
                                {showConfirmar ? <FaEyeSlash /> : <FaEye />}
                            </button>
                        </div>
                    </div>

                    <div className="perfil-actions">
                        <button type="submit" className="btn-salvar" disabled={loadingSenha}>
                            {loadingSenha ? 'Atualizando...' : 'Atualizar Minha Senha'}
                        </button>
                    </div>
                </form>
            )}

            <div className="danger-zone">
                <h3>Zona de Perigo</h3>
                <p>A exclusão da conta é irreversível.</p>
                <button type="button" onClick={handleDeleteAccount} className="btn-excluir-conta">
                    Excluir Minha Conta
                </button>
            </div>
        </div>
    );
};

export default MeuPerfil;
